import React, { useState, useEffect, useCallback } from 'react';
import { 
  TrendingUp, TrendingDown, 
  DollarSign, Clock, Activity, Zap,
  BarChart3, Wallet, Play, Pause, RefreshCw, Gauge,
  Wifi, WifiOff, CheckCircle, XCircle, AlertTriangle, Bell
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { apiGetStatus, apiTrade, apiGetHistory } from '../utils/api';
import { AUTH_FLAG, PW_KEY } from './LoginGate';

// Token configurations. Mirrors api/_lib/tokens.js — the server is authoritative
// and rejects anything not in its own registry; these values drive the UI defaults.
const TOKEN_CONFIGS = {
  payu: {
    name: 'PAYU Coin',
    symbol: 'PAYU',
    address: '0x9aeb2e6dd8d55e14292acfcfc4077e33106e4144',
    pair: 'PAYU/BNB',
    color: '#10B981',
    logo: '🪙',
    amountBNB: '0.15',
    slippage: '2',
    intervalMinutes: 240,
    cron: true // the only token with a server-side cron (vercel.json); others are manual-only
  },
  irena: {
    name: 'IRENA Green Energy',
    symbol: 'IRENA',
    address: '0x9eeb6c5ff183e6001c65a12d70026b900ae76781',
    pair: 'IRENA/BNB',
    color: '#22D3EE',
    logo: '🌱',
    amountBNB: '0.05',
    slippage: '10', // taxed token: needs headroom above the untaxed router quote
    intervalMinutes: 1440
  },
  aifx: {
    name: 'AI FOREX',
    symbol: 'AIFX',
    address: '0x125bff3470677666c52536a41edff897cbb8b884',
    pair: 'AIFX/BNB',
    color: '#A78BFA',
    logo: '🤖',
    amountBNB: '0.1',
    slippage: '10',
    intervalMinutes: 1440
  },
  hermes: {
    name: 'Hermes AI Investment Fund',
    symbol: 'HERMES',
    address: '0x9495ab3549338bf14ad2f86cbcf79c7b574bba37',
    pair: 'HERMES/BNB',
    color: '#F59E0B',
    logo: '🔱',
    amountBNB: '0.075',
    slippage: '10', // taxed token: ~3% buy / ~1.78% sell
    intervalMinutes: 1440
  },
  ride: {
    name: 'AI RIDE',
    symbol: 'RIDE',
    address: '0xfdff84c4d7f7a0238ed7852808177a520b9f89a2',
    pair: 'RIDE/BNB',
    color: '#EF4444',
    logo: '🛵',
    amountBNB: '0.1',
    slippage: '10', // shallow pool — big price impact at 0.1 BNB
    intervalMinutes: 1440 // once a day
  }
};

// ---- Time-bucketed P&L tracker (localStorage-backed, survives page reloads) ----
// Keyed per token: the baseline snapshot holds that token's balance, so a PAYU
// baseline compared against an AIFX portfolio would report a phantom loss.
const pnlKey = (tokenKey) => `payubot_pnl_v1_${tokenKey}`;
const baselineKey = (tokenKey) => `payubot_baseline_${tokenKey}`;
const emptyPnl = () => ({
  netWorth: { allTime: null, day: { key: '', usd: 0 }, month: { key: '', usd: 0 } },
  botCost: { allTimeBNB: 0, day: { key: '', bnb: 0 }, month: { key: '', bnb: 0 } }
});
const loadPnl = (key) => {
  try { return JSON.parse(localStorage.getItem(key)) || emptyPnl(); }
  catch (e) { return emptyPnl(); }
};
const savePnl = (key, s) => { try { localStorage.setItem(key, JSON.stringify(s)); } catch (e) {} };

// Generic localStorage helpers so state survives page reloads
const lsGet = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); }
  catch (e) { return fallback; }
};
const lsSet = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) {} };
// Net-worth P&L = current portfolio USD minus the baseline captured at the start of each period
const trackNetWorth = (store, currentUSD, dayKey, monthKey) => {
  const nw = store.netWorth;
  if (nw.allTime == null) nw.allTime = currentUSD;
  if (nw.day.key !== dayKey) nw.day = { key: dayKey, usd: currentUSD };
  if (nw.month.key !== monthKey) nw.month = { key: monthKey, usd: currentUSD };
  return {
    daily: currentUSD - nw.day.usd,
    monthly: currentUSD - nw.month.usd,
    allTime: currentUSD - nw.allTime
  };
};
// Bot cost = accumulated gas/fee spend per period (always a loss)
const trackBotCost = (store, feeBNB, dayKey, monthKey) => {
  const bc = store.botCost;
  if (bc.day.key !== dayKey) bc.day = { key: dayKey, bnb: 0 };
  if (bc.month.key !== monthKey) bc.month = { key: monthKey, bnb: 0 };
  bc.day.bnb += feeBNB;
  bc.month.bnb += feeBNB;
  bc.allTimeBNB += feeBNB;
};

const CryptoTradingApp = ({ tokenKey = 'payu' }) => {
  // Which token this page trades. Fixed by the route; App.js remounts on change.
  const selectedToken = tokenKey;
  const PNL_KEY = pnlKey(tokenKey);
  const BASELINE_KEY = baselineKey(tokenKey);
  const [isConnected, setIsConnected] = useState(false); // auto-true once /api/status loads the server wallet
  // SECURITY: never hardcode a wallet address or private key. Enter them in the UI at runtime.
  const [walletAddress, setWalletAddress] = useState('');
  const [isBotRunning, setIsBotRunning] = useState(false);
  // Trade password (validated server-side). Never the private key — that stays
  // on the server. Persisted in localStorage so it isn't re-typed every reload.
  // Password is set once at login and read from storage here; never edited in-panel.
  const [tradePassword] = useState(() => lsGet('payubot_pw', ''));
  
  const [balances, setBalances] = useState({
    bnb: '0.000',
    token: '0.000',
    usdValue: '0.00'
  });
  
  // Initial balances for profit/loss tracking. Persisted so the baseline (and
  // "tracking started" time) survives reloads instead of resetting every refresh.
  const [initialBalances, setInitialBalances] = useState(() => {
    const v = lsGet(baselineKey(tokenKey), null);
    if (v) return { ...v, timestamp: v.timestamp ? new Date(v.timestamp) : null };
    return { bnb: '0', token: '0', bnbValue: '0.00', tokenValue: '0.00', totalUSDValue: '0.00', timestamp: null };
  });
  
  // Profit/Loss tracking
  const [profitLoss, setProfitLoss] = useState({
    bnb: {
      initial: '0.000',
      current: '0.000',
      change: '0.000',
      changePercent: '0.00',
      isPositive: true
    },
    token: {
      initial: '0.000',
      current: '0.000',
      change: '0.000',
      changePercent: '0.00',
      isPositive: true
    },
    totalUSD: {
      initial: '0.00',
      current: '0.00',
      change: '0.00',
      changePercent: '0.00',
      isPositive: true
    }
  });
  
  const [tradingStats, setTradingStats] = useState(() => lsGet('payubot_stats', {
    totalTrades: 0,
    successRate: 100,
    totalProfit: '0.000',
    dailyVolume: '0.00'
  }));

  // Persisted history — survives reloads (real on-chain trades accumulate here)
  const [tradingHistory, setTradingHistory] = useState(() => lsGet('payubot_history', []));
  const [notifications, setNotifications] = useState([]);
  const [networkStatus, setNetworkStatus] = useState({
    blockNumber: 0,
    gasPriceGwei: 0,
    isOffPeak: false,
    isLowFee: false,
    isUltraLowFee: false,
    rpcHealth: {},
    currentRpc: ''
  });
  
  const [feeStats, setFeeStats] = useState(() => lsGet('payubot_fees', {
    totalFees: '0.000000',
    totalSavings: '0.000000',
    averageFee: '0.000000',
    totalTransactions: 0,
    savingsPercentage: 0
  }));
  
  const [botSettings, setBotSettings] = useState({
    tradeAmount: TOKEN_CONFIGS[tokenKey].amountBNB,
    slippage: TOKEN_CONFIGS[tokenKey].slippage,
    maxGasPrice: '10', // Back to normal 10 Gwei
    intervalMinutes: TOKEN_CONFIGS[tokenKey].intervalMinutes,
    stopLoss: '5',
    takeProfit: '10',
    autoTradingEnabled: true,
    // Ultra Low Fee Settings
    gasSettings: {
      gasPrice: 'ultraLow',     // Back to ultraLow for gas savings
      maxFeePerTx: '0.01',      // Back to 0.01 BNB for savings
      onlyOffPeak: false,       // Disabled off-peak restriction for faster trading
      waitForLowFees: true,     // Back to waiting for low fees
      emergencyStop: false,      // Emergency stop trading
      priorityExecution: false,  // Disabled priority execution
      instantExecution: false    // Disabled instant execution
    }
  });
  
  const [manualTradeAmount, setManualTradeAmount] = useState(TOKEN_CONFIGS[tokenKey].amountBNB);

  // Monthly budget guard - auto-trading stops when the USD cap is reached, resets each month
  const [spendGuard, setSpendGuard] = useState({
    monthKey: new Date().toISOString().slice(0, 7), // YYYY-MM
    spentBNB: 0,        // accumulated BNB gas spend this month
    capUSD: 500         // hard monthly budget cap in USD
  });

  // Live BNB/USD price (for pricing bot-cost in USD)
  const [bnbUsdPrice, setBnbUsdPrice] = useState(560);

  // Time-bucketed P&L shown on the panel: wallet net-worth change + bot running cost
  const [pnlView, setPnlView] = useState({
    netWorth: { daily: 0, monthly: 0, allTime: 0 },       // USD
    botCostBNB: { daily: 0, monthly: 0, allTime: 0 },     // BNB (gas only)
    tradeCostBNB: { daily: 0, monthly: 0, allTime: 0 }    // BNB, server counters: both legs + gas
  });
  // Server-side bot health for this token (cron last run / last error) and when
  // the running cost counters started.
  const [serverBot, setServerBot] = useState({ status: null, costSince: null });

  // Price P&L: mark-to-market gain/loss from price movement on current holdings
  // (deposit-proof — only prices move it, not funding the wallet).
  const [pricePnl, setPricePnl] = useState({ usd: 0, pct: 0, isPositive: true, ready: false });

  // Persist state across reloads (baseline, history, stats, fees). The password
  // is written by LoginGate, not here.
  useEffect(() => { lsSet(BASELINE_KEY, initialBalances); }, [BASELINE_KEY, initialBalances]);
  useEffect(() => { lsSet('payubot_history', tradingHistory); }, [tradingHistory]);
  useEffect(() => { lsSet('payubot_stats', tradingStats); }, [tradingStats]);
  useEffect(() => { lsSet('payubot_fees', feeStats); }, [feeStats]);

  // Record a completed trade's fee into the bot-cost buckets
  const recordBotCost = useCallback((feeBNB) => {
    const f = parseFloat(feeBNB) || 0;
    if (!f) return;
    const now = new Date();
    const dayKey = now.toISOString().slice(0, 10);
    const monthKey = now.toISOString().slice(0, 7);
    const store = loadPnl(PNL_KEY);
    trackBotCost(store, f, dayKey, monthKey);
    savePnl(PNL_KEY, store);
    setPnlView(prev => ({
      ...prev,
      botCostBNB: { daily: store.botCost.day.bnb, monthly: store.botCost.month.bnb, allTime: store.botCost.allTimeBNB }
    }));
  }, [PNL_KEY]);

  const [lastTradeTime, setLastTradeTime] = useState(null);
  const [nextTradeTime, setNextTradeTime] = useState(null);

  const currentToken = TOKEN_CONFIGS[selectedToken];

  // Read-only helpers for live prices + network status (no private key involved).
  // All trading/signing happens server-side via /api/trade.
  const {
    getBNBPrice,
    getNetworkStatus,
    getPublicProvider
  } = require('../utils/web3');

  // Poll live BNB/USD price (used to price bot-cost in USD)
  useEffect(() => {
    let alive = true;
    const pull = () => getBNBPrice().then(p => { if (alive && p) setBnbUsdPrice(parseFloat(p)); }).catch(() => {});
    pull();
    const id = setInterval(pull, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // On mount, restore bot-cost buckets from storage (respecting current day/month)
  useEffect(() => {
    const store = loadPnl(PNL_KEY);
    const now = new Date();
    const dayKey = now.toISOString().slice(0, 10);
    const monthKey = now.toISOString().slice(0, 7);
    setPnlView(prev => ({
      ...prev,
      botCostBNB: {
        daily: store.botCost.day.key === dayKey ? store.botCost.day.bnb : 0,
        monthly: store.botCost.month.key === monthKey ? store.botCost.month.bnb : 0,
        allTime: store.botCost.allTimeBNB
      }
    }));
  }, [PNL_KEY]);

  // Recompute wallet net-worth P&L whenever portfolio USD value changes
  useEffect(() => {
    const usd = parseFloat(balances.totalUSDValue || '0');
    if (!usd) return;
    const now = new Date();
    const dayKey = now.toISOString().slice(0, 10);
    const monthKey = now.toISOString().slice(0, 7);
    const store = loadPnl(PNL_KEY);
    const nw = trackNetWorth(store, usd, dayKey, monthKey);
    savePnl(PNL_KEY, store);
    setPnlView(prev => ({ ...prev, netWorth: nw }));
  }, [PNL_KEY, balances.totalUSDValue]);

  // Notification system
  const addNotification = useCallback((type, message) => {
    const id = Date.now() + Math.random(); // Add random to ensure unique keys
    setNotifications(prev => [...prev, { id, type, message, timestamp: new Date() }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  // Clear P&L baseline + history/stats (local only — never touches the wallet)
  const handleResetTracking = useCallback(() => {
    [BASELINE_KEY, 'payubot_history', 'payubot_stats', 'payubot_fees', PNL_KEY].forEach(k => {
      try { localStorage.removeItem(k); } catch (e) {}
    });
    setInitialBalances({ bnb: '0', token: '0', bnbValue: '0.00', tokenValue: '0.00', totalUSDValue: '0.00', timestamp: null });
    setTradingHistory([]);
    setTradingStats({ totalTrades: 0, successRate: 100, totalProfit: '0.000', dailyVolume: '0.00' });
    setFeeStats({ totalFees: '0.000000', totalSavings: '0.000000', averageFee: '0.000000', totalTransactions: 0, savingsPercentage: 0 });
    setPnlView({ netWorth: { daily: 0, monthly: 0, allTime: 0 }, botCostBNB: { daily: 0, monthly: 0, allTime: 0 }, tradeCostBNB: { daily: 0, monthly: 0, allTime: 0 } });
    setPricePnl({ usd: 0, pct: 0, isPositive: true, ready: false });
    addNotification('info', 'Tracking reset — a fresh baseline will be captured on the next balance update');
  }, [BASELINE_KEY, PNL_KEY, addNotification]);

  // Pull the server trade log (cron + manual) and drive the History table,
  // Bot Cost buckets and fee stats from it — the single source of truth.
  const refreshHistory = useCallback(async () => {
    try {
      const { trades, cronStatus, costs } = await apiGetHistory(300);
      if (!Array.isArray(trades)) return;

      // One shared log across all three tokens (same wallet). Trades recorded
      // before the multi-token rollout carry no `token` field — those are PAYU.
      setTradingHistory(trades.slice(0, 30).map((t) => ({
        id: `${t.ts}-${t.type}`,
        time: t.ts ? new Date(t.ts).toLocaleString() : '',
        type: t.type,
        token: t.token || 'PAYU',
        amount: `${t.amountBNB} BNB`,
        price: '-',
        status: 'Completed',
        hash: t.hash,
        fee: t.actualFee,
        gasPriceGwei: t.gasPriceGwei,
        savings: t.savings && t.savings.savings ? t.savings.savings : '0'
      })));

      const now = new Date();
      const dayKey = now.toISOString().slice(0, 10);
      const monthKey = now.toISOString().slice(0, 7);
      let dFee = 0, mFee = 0, aFee = 0, totalSavings = 0;
      trades.forEach((t) => {
        const fee = parseFloat(t.actualFee) || 0;
        const sav = t.savings && t.savings.savings ? parseFloat(t.savings.savings) || 0 : 0;
        const iso = new Date(t.ts || Date.now()).toISOString();
        aFee += fee; totalSavings += sav;
        if (iso.slice(0, 7) === monthKey) mFee += fee;
        if (iso.slice(0, 10) === dayKey) dFee += fee;
      });
      // Trading cost comes from server-side running counters (true all-time, both
      // the BNB and the token leg — see api/_lib/cost.js). Recomputing it from this
      // capped trade list would silently cover only the last few weeks.
      const cost = (costs && costs[currentToken.symbol]) || { day: 0, month: 0, all: 0, since: null };
      setPnlView((prev) => ({
        ...prev,
        botCostBNB: { daily: dFee, monthly: mFee, allTime: aFee },
        tradeCostBNB: { daily: cost.day, monthly: cost.month, allTime: cost.all }
      }));
      setServerBot({ status: (cronStatus && cronStatus[currentToken.symbol]) || null, costSince: cost.since });
      setTradingStats((prev) => ({ ...prev, totalTrades: trades.length }));
      setFeeStats({
        totalFees: aFee.toFixed(6),
        totalSavings: totalSavings.toFixed(6),
        averageFee: trades.length ? (aFee / trades.length).toFixed(6) : '0.000000',
        totalTransactions: trades.length,
        savingsPercentage: aFee > 0 ? ((totalSavings / aFee) * 100).toFixed(1) : 0
      });
    } catch (e) {
      // KV not configured or fetch failed — keep whatever is shown
    }
  }, [currentToken.symbol]);

  // Poll the server trade log every 20s (and once on mount)
  useEffect(() => {
    refreshHistory();
    const id = setInterval(refreshHistory, 20000);
    return () => clearInterval(id);
  }, [refreshHistory]);

  // Compute the profit/loss view from a fresh balance snapshot vs the baseline
  const applyProfitLoss = useCallback((bnbBalance, tokenBalance, totalUSDValue, baseline) => {
    const initBNB = parseFloat(baseline.bnb || '0');
    const initToken = parseFloat(baseline.token || '0');
    const initUSD = parseFloat(baseline.totalUSDValue || '0');
    const bnbChange = parseFloat(bnbBalance) - initBNB;
    const tokenChange = parseFloat(tokenBalance) - initToken;
    const totalChange = totalUSDValue - initUSD;
    setProfitLoss({
      bnb: {
        initial: baseline.bnb, current: bnbBalance,
        change: bnbChange.toFixed(6),
        changePercent: initBNB > 0 ? ((bnbChange / initBNB) * 100).toFixed(2) : '0.00',
        isPositive: bnbChange >= 0
      },
      token: {
        initial: baseline.token, current: tokenBalance,
        change: tokenChange.toFixed(6),
        changePercent: initToken > 0 ? ((tokenChange / initToken) * 100).toFixed(2) : '0.00',
        isPositive: tokenChange >= 0
      },
      totalUSD: {
        initial: baseline.totalUSDValue || '0.00', current: totalUSDValue.toFixed(2),
        change: totalChange.toFixed(2),
        changePercent: initUSD > 0 ? ((totalChange / initUSD) * 100).toFixed(2) : '0.00',
        isPositive: totalChange >= 0
      }
    });
  }, []);

  // Price P&L = value change from price movement alone, on current holdings.
  // Deposits don't distort it because it multiplies price delta by current qty.
  const applyPricePnl = useCallback((s, baseline, curTokenUsd, curBnbUsd) => {
    const baseTokenUsd = parseFloat(baseline.tokenPriceUsd);
    const baseBnbUsd = parseFloat(baseline.bnbPriceUsd);
    if (!(baseTokenUsd >= 0) || !(baseBnbUsd >= 0)) { setPricePnl({ usd: 0, pct: 0, isPositive: true, ready: false }); return; }
    const tokenQty = parseFloat(s.token) || 0;
    const bnbQty = parseFloat(s.bnb) || 0;
    const usd = tokenQty * (curTokenUsd - baseTokenUsd) + bnbQty * (curBnbUsd - baseBnbUsd);
    const baseVal = parseFloat(baseline.totalUSDValue) || 0;
    setPricePnl({ usd, pct: baseVal > 0 ? (usd / baseVal) * 100 : 0, isPositive: usd >= 0, ready: true });
  }, []);

  // Real-time balances via the secure /api/status endpoint (no key in browser)
  useEffect(() => {
    let alive = true;
    const updateBalances = async () => {
      try {
        const s = await apiGetStatus(currentToken.address);
        if (!alive) return;
        const totalUSDValue = parseFloat(s.totalUSDValue);
        setBalances({
          bnb: s.bnb,
          token: s.token,
          bnbUSDValue: s.bnbUSDValue,
          tokenUSDValue: s.tokenUSDValue,
          totalUSDValue: s.totalUSDValue
        });
        if (s.address) {
          setWalletAddress(s.address);
          setIsConnected(true); // server manages the wallet — auto-connect once status loads
        }

        // Current unit prices in USD (server gives token price in BNB + BNB/USD)
        const curBnbUsd = parseFloat(s.bnbUsdPrice) || 0;
        const curTokenUsd = (parseFloat(s.tokenPriceBNB) || 0) * curBnbUsd;

        if (!initialBalances.timestamp) {
          // Capture the real baseline the first time we get live numbers
          const baseline = {
            bnb: s.bnb, token: s.token,
            bnbValue: s.bnbUSDValue, tokenValue: s.tokenUSDValue,
            totalUSDValue: s.totalUSDValue, timestamp: new Date(),
            tokenPriceUsd: String(curTokenUsd), bnbPriceUsd: String(curBnbUsd)
          };
          setInitialBalances(baseline);
          applyProfitLoss(s.bnb, s.token, totalUSDValue, baseline);
          applyPricePnl(s, baseline, curTokenUsd, curBnbUsd);
        } else {
          // Backfill a price baseline for trackers created before this feature
          let base = initialBalances;
          if (!initialBalances.tokenPriceUsd) {
            base = { ...initialBalances, tokenPriceUsd: String(curTokenUsd), bnbPriceUsd: String(curBnbUsd) };
            setInitialBalances(base);
          }
          applyProfitLoss(s.bnb, s.token, totalUSDValue, base);
          applyPricePnl(s, base, curTokenUsd, curBnbUsd);
        }
      } catch (error) {
        console.error('Failed to update balances:', error);
        // Keep last known values; don't inject fake data
      }
    };

    updateBalances();
    const interval = setInterval(updateBalances, 30000); // Update every 30 seconds
    return () => { alive = false; clearInterval(interval); };
  }, [currentToken.address, initialBalances, applyProfitLoss, applyPricePnl]);

  // Real-time network status updates (public provider — no private key needed)
  useEffect(() => {
    const updateNetworkStatus = async () => {
      try {
        const provider = await getPublicProvider();
        const status = await getNetworkStatus(provider);
        setNetworkStatus(status);
      } catch (error) {
        console.error('Failed to update network status:', error);
        // Keep last known status; don't inject fake block/gas numbers
      }
    };

    updateNetworkStatus();
    const interval = setInterval(updateNetworkStatus, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Automatic trading logic
  useEffect(() => {
    if (isConnected && isBotRunning && botSettings.autoTradingEnabled && tradePassword) {
      const interval = setInterval(async () => {
        await executeAutomaticTrade();
      }, botSettings.intervalMinutes * 60 * 1000); // Convert minutes to milliseconds

      // Set next trade time
      const nextTrade = new Date();
      nextTrade.setMinutes(nextTrade.getMinutes() + botSettings.intervalMinutes);
      setNextTradeTime(nextTrade);

      return () => clearInterval(interval);
    }
  }, [isConnected, isBotRunning, botSettings.intervalMinutes, botSettings.autoTradingEnabled, tradePassword]);

  // Log out: clear the session + stored password, then return to the login gate.
  const handleLogout = useCallback(() => {
    try {
      localStorage.removeItem(AUTH_FLAG);
      localStorage.removeItem(PW_KEY);
    } catch (e) { /* storage disabled */ }
    window.location.href = '/';
  }, []);

  // Connect = fetch the server-managed wallet status. The private key stays on
  // the server; here we only receive the public address + balances.
  const handleConnect = useCallback(async () => {
    try {
      const s = await apiGetStatus(currentToken.address);
      setWalletAddress(s.address);
      setIsConnected(true);
      addNotification('success', `Wallet connected: ${s.address.slice(0, 6)}...${s.address.slice(-4)}`);
      if (!tradePassword) {
        addNotification('info', 'Enter your trade password to enable Buy/Sell and the bot');
      }
    } catch (e) {
      setIsConnected(false);
      addNotification('error', 'Connect failed: ' + e.message + ' (is PRIVATE_KEY set on the server?)');
    }
  }, [currentToken.address, tradePassword, addNotification]);

  // Execute automatic trading cycle via the server (SELL -> wait 5s -> BUY same amount).
  // The server signs both txs; the private key never touches the browser.
  const executeAutomaticTrade = useCallback(async () => {
    if (!tradePassword) {
      addNotification('error', 'Enter your trade password to run trades');
      return;
    }
    try {
      // Monthly budget guard: stop auto-trading once the USD cap is hit (resets on month change)
      const nowKey = new Date().toISOString().slice(0, 7);
      const bnbUsd = bnbUsdPrice;
      const spentBNB = spendGuard.monthKey === nowKey ? spendGuard.spentBNB : 0;
      if (spendGuard.monthKey !== nowKey) {
        setSpendGuard(prev => ({ ...prev, monthKey: nowKey, spentBNB: 0 }));
      }
      if (spentBNB * bnbUsd >= spendGuard.capUSD) {
        setIsBotRunning(false);
        addNotification('error', `Monthly budget of $${spendGuard.capUSD} reached (spent ~$${(spentBNB * bnbUsd).toFixed(2)}). Auto-trading stopped until next month.`);
        return;
      }

      // Same amount is used for both the sell and the matching buy
      const tradeAmount = currentToken.amountBNB;
      addNotification('info', `Starting trade cycle: SELL ${tradeAmount} BNB -> wait 5s -> BUY ${tradeAmount} BNB`);

      const { trades } = await apiTrade({
        action: 'cycle',
        amount: tradeAmount,
        password: tradePassword,
        slippage: parseFloat(botSettings.slippage),
        gasSettings: botSettings.gasSettings,
        token: currentToken.address
      });
      await refreshHistory(); // server KV is the source of truth for history/fees

      setLastTradeTime(new Date());
      const nextTrade = new Date();
      nextTrade.setMinutes(nextTrade.getMinutes() + botSettings.intervalMinutes);
      setNextTradeTime(nextTrade);

      addNotification('success', `Trade cycle completed: SELL + BUY ${tradeAmount} BNB (${trades.length} tx)`);
    } catch (error) {
      addNotification('error', 'Automatic trading failed: ' + error.message);
    }
    // recordTrade is defined below; referenced via closure, intentionally omitted from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradePassword, bnbUsdPrice, spendGuard, botSettings.slippage, botSettings.gasSettings, botSettings.intervalMinutes, currentToken.address, currentToken.amountBNB, addNotification]);

  // Manual BUY via the server (signed server-side)
  const executeBuyOrder = useCallback(async (amount) => {
    if (!tradePassword) {
      addNotification('error', 'Enter your trade password first');
      return null;
    }
    try {
      addNotification('info', `Buying ${amount} BNB worth of ${currentToken.symbol}...`);
      const { trades } = await apiTrade({
        action: 'buy', amount, password: tradePassword,
        slippage: parseFloat(botSettings.slippage),
        gasSettings: botSettings.gasSettings, token: currentToken.address
      });
      await refreshHistory(); // server KV is the source of truth for history/fees
      const t = trades[0];
      addNotification('success', `Buy order completed! Fee: ${t.actualFee} BNB (${t.savings.savingsPercentage}% savings)`);
      return t;
    } catch (error) {
      addNotification('error', 'Buy order failed: ' + error.message);
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradePassword, currentToken.address, currentToken.symbol, botSettings.gasSettings, botSettings.slippage, addNotification]);

  // Manual SELL via the server (signed server-side)
  const executeSellOrder = useCallback(async (amount) => {
    if (!tradePassword) {
      addNotification('error', 'Enter your trade password first');
      return null;
    }
    try {
      addNotification('info', `Selling ${amount} BNB worth of ${currentToken.symbol}...`);
      const { trades } = await apiTrade({
        action: 'sell', amount, password: tradePassword,
        slippage: parseFloat(botSettings.slippage),
        gasSettings: botSettings.gasSettings, token: currentToken.address
      });
      await refreshHistory(); // server KV is the source of truth for history/fees
      const t = trades[0];
      addNotification('success', `Sell order completed! Fee: ${t.actualFee} BNB (${t.savings.savingsPercentage}% savings)`);
      return t;
    } catch (error) {
      addNotification('error', 'Sell order failed: ' + error.message);
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradePassword, currentToken.address, currentToken.symbol, botSettings.gasSettings, botSettings.slippage, addNotification]);

  // Update fee statistics
  const updateFeeStats = useCallback((actualFee, savings) => {
    // Accumulate monthly BNB spend for the budget guard (auto + manual both counted, resets each month)
    setSpendGuard(prev => {
      const nowKey = new Date().toISOString().slice(0, 7);
      const base = prev.monthKey === nowKey ? prev.spentBNB : 0;
      return { ...prev, monthKey: nowKey, spentBNB: base + (parseFloat(actualFee) || 0) };
    });

    // Feed the time-bucketed bot-cost P&L (daily / monthly / all-time)
    recordBotCost(actualFee);

    setFeeStats(prev => {
      const newTotalFees = parseFloat(prev.totalFees) + parseFloat(actualFee);
      const newTotalSavings = parseFloat(prev.totalSavings) + parseFloat(savings.savings);
      const newTotalTransactions = prev.totalTransactions + 1;
      const newAverageFee = newTotalFees / newTotalTransactions;
      const newSavingsPercentage = newTotalSavings > 0 ? (newTotalSavings / newTotalFees) * 100 : 0;
      
      return {
        totalFees: newTotalFees.toFixed(6),
        totalSavings: newTotalSavings.toFixed(6),
        averageFee: newAverageFee.toFixed(6),
        totalTransactions: newTotalTransactions,
        savingsPercentage: newSavingsPercentage.toFixed(1)
      };
    });
  }, [recordBotCost]);

  // Append a server trade result to the history + stats + fee/cost trackers
  const recordTrade = useCallback((t) => {
    const entry = {
      id: Date.now() + Math.random(),
      time: new Date().toLocaleString(),
      type: t.type,
      amount: `${t.amountBNB} BNB`,
      price: '-',
      status: 'Completed',
      hash: t.hash,
      fee: t.actualFee,
      gasPriceGwei: t.gasPriceGwei,
      savings: t.savings.savings
    };
    setTradingHistory(prev => [entry, ...prev.slice(0, 19)]); // keep last 20
    setTradingStats(prev => ({ ...prev, totalTrades: prev.totalTrades + 1 }));
    updateFeeStats(t.actualFee, t.savings);
  }, [updateFeeStats]);

  // Refresh balances + P&L right after a manual trade (via /api/status)
  const forceUpdateProfitLoss = useCallback(async () => {
    try {
      const s = await apiGetStatus(currentToken.address);
      const totalUSDValue = parseFloat(s.totalUSDValue);
      setBalances({
        bnb: s.bnb, token: s.token,
        bnbUSDValue: s.bnbUSDValue, tokenUSDValue: s.tokenUSDValue,
        totalUSDValue: s.totalUSDValue
      });
      const baseline = initialBalances.timestamp ? initialBalances : {
        bnb: s.bnb, token: s.token,
        bnbValue: s.bnbUSDValue, tokenValue: s.tokenUSDValue,
        totalUSDValue: s.totalUSDValue, timestamp: new Date()
      };
      if (!initialBalances.timestamp) setInitialBalances(baseline);
      applyProfitLoss(s.bnb, s.token, totalUSDValue, baseline);
    } catch (error) {
      console.error('Failed to force update profit/loss:', error);
    }
  }, [currentToken.address, initialBalances, applyProfitLoss]);

  // Modern Header Component with Enhanced Network Status
  const Header = () => (
    <header className="glass border-b border-gray-800 backdrop-blur-lg bg-opacity-95 sticky top-0 z-50">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                <BarChart3 className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">CryptoBot Pro</h1>
                <p className="text-xs text-gray-300">Ultra Low Fee Trading Platform</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Network Status */}
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                networkStatus.isUltraLowFee ? 'bg-green-400' :
                networkStatus.isLowFee ? 'bg-yellow-400' : 'bg-red-400'
              }`}></div>
              <span className="text-xs text-gray-400">
                {networkStatus.isUltraLowFee ? 'Ultra Low' :
                 networkStatus.isLowFee ? 'Low' : 'High'} Gas
              </span>
              {networkStatus.isOffPeak && (
                <span className="text-xs text-blue-400">Off-Peak</span>
              )}
            </div>
            
            {/* RPC Status */}
            <div className="flex items-center space-x-1">
              {networkStatus.currentRpc ? (
                <Wifi className="w-4 h-4 text-green-400" />
              ) : (
                <WifiOff className="w-4 h-4 text-red-400" />
              )}
              <span className="text-xs text-gray-400">
                Block #{networkStatus.blockNumber}
              </span>
            </div>
            
            {/* Token pages — same wallet, same panel, different token */}
            <nav className="flex items-center space-x-1 bg-gray-800 p-1 rounded-xl border border-gray-700">
              {Object.entries(TOKEN_CONFIGS).map(([key, config]) => (
                <NavLink
                  key={key}
                  to={`/${key}`}
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'
                    }`
                  }
                >
                  {config.logo} {config.symbol}
                </NavLink>
              ))}
              <NavLink
                to="/maradona"
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`
                }
              >
                🧉 Maradona
              </NavLink>
            </nav>
            
            {/* Wallet: the private key lives on the server. The trade password
                is set once at login (LoginGate) and stored per-origin. */}
            <div className="flex items-center space-x-2">
              {isConnected && (
                <div className="flex items-center space-x-2 px-3 py-2 bg-green-600 rounded-xl">
                  <Wallet className="w-4 h-4 text-white" />
                  <span className="text-white font-medium text-sm">
                    {walletAddress ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` : 'Connected'}
                  </span>
                  <div className="flex items-center space-x-1 px-2 py-1 bg-green-700 rounded-lg">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-xs text-green-400">Live</span>
                  </div>
                </div>
              )}
              {!isConnected && (
                <button type="button" onClick={handleConnect} className="btn-success px-4 py-2 text-sm">
                  Connect
                </button>
              )}
              <button type="button" onClick={handleLogout} className="text-gray-400 hover:text-white text-xs px-3 py-2 rounded-xl border border-gray-700">
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );

  // Notification Component
  const NotificationCenter = () => (
    <div className="fixed top-20 right-6 z-50 space-y-3">
      {notifications.map(notification => (
        <div
          key={notification.id}
          className={`notification-modern max-w-sm transform transition-all duration-300 ${
            notification.type === 'success' ? 'border-green-500' :
            notification.type === 'error' ? 'border-red-500' :
            notification.type === 'warning' ? 'border-yellow-500' : 'border-blue-500'
          }`}
        >
          <div className="flex items-center space-x-3">
            {notification.type === 'success' && <CheckCircle className="w-6 h-6 text-green-500" />}
            {notification.type === 'error' && <XCircle className="w-6 h-6 text-red-500" />}
            {notification.type === 'warning' && <AlertTriangle className="w-6 h-6 text-yellow-500" />}
            {notification.type === 'info' && <Bell className="w-6 h-6 text-blue-500" />}
            <p className="text-white text-sm font-medium">{notification.message}</p>
          </div>
        </div>
      ))}
    </div>
  );

  // Enhanced Stats Card
  const StatsCard = ({ title, value, subValue, icon: Icon, color, trend }) => (
    <div className="card-modern hover:border-gray-600 transition-all duration-300 transform hover:scale-105">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center shadow-lg`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
        {trend && (
          <div className={`flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-semibold ${
            trend > 0 ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'
          }`}>
            {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <h3 className="text-gray-300 text-xs font-medium mb-2">{title}</h3>
      <p className="text-white text-2xl font-bold mb-1">{value}</p>
      {subValue && <p className="text-gray-400 text-xs">{subValue}</p>}
    </div>
  );



  // Enhanced Bot Control Panel with Ultra Low Fee Settings
  const BotControlPanel = () => (
    <div className="card-modern">
      <h3 className="text-white text-lg font-bold mb-4 flex items-center">
        <Zap className="w-5 h-5 mr-2 text-yellow-400" />
        ⚡ FLASH Ultra Low Fee Trading Bot - {currentToken.name}
      </h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bot Status & Control */}
        <div className="space-y-4">
          <div className="bg-gray-900 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-400 font-medium">Bot Status</span>
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                isBotRunning ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'
              }`}>
                <div className={`w-2 h-2 rounded-full ${isBotRunning ? 'bg-green-400' : 'bg-red-400'}`}></div>
                <span>{isBotRunning ? 'Active' : 'Stopped'}</span>
              </div>
            </div>
            
            {/* Trading Info */}
            <div className="mb-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Server bot (cron):</span>
                {!currentToken.cron ? (
                  <span className="text-gray-500">Kapali — sadece manuel</span>
                ) : !serverBot.status ? (
                  <span className="text-gray-500">Henuz kayit yok</span>
                ) : serverBot.status.lastErrorAt && serverBot.status.lastErrorAt >= (serverBot.status.lastOk || 0) ? (
                  <span className="text-red-400 text-right" title={serverBot.status.lastError}>
                    HATA {new Date(serverBot.status.lastErrorAt).toLocaleString()}: {serverBot.status.lastError}
                  </span>
                ) : (
                  <span className="text-green-400">OK — son basarili {new Date(serverBot.status.lastOk).toLocaleString()}</span>
                )}
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Trade Amount:</span>
                <span className="text-white">{currentToken.amountBNB} BNB (SELL → same BUY)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Trading Mode:</span>
                <span className="text-yellow-400 font-bold">SELL → 5s → BUY</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Slippage:</span>
                <span className="text-green-400 font-bold">{botSettings.slippage}% (NORMAL)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Gas Priority:</span>
                <span className="text-green-400 font-bold">ULTRA LOW (3 Gwei)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Interval:</span>
                <span className="text-white">Every {botSettings.intervalMinutes % 60 === 0 ? `${botSettings.intervalMinutes / 60} hour(s)` : `${botSettings.intervalMinutes} minute(s)`}</span>
              </div>
              {nextTradeTime && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Next Trade:</span>
                  <span className="text-blue-400">{nextTradeTime.toLocaleString()}</span>
                </div>
              )}
              {lastTradeTime && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Last Trade:</span>
                  <span className="text-gray-300">{lastTradeTime.toLocaleString()}</span>
                </div>
              )}
            </div>
            
            {/* Control Buttons */}
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  if (!isBotRunning) {
                    // Trading requires the trade password (the wallet auto-connects)
                    if (!tradePassword) {
                      addNotification('error', 'Enter your trade password (top-right) to start the bot.');
                      return;
                    }
                    setIsBotRunning(true);
                    addNotification('success', `Bot started — running the first trade now, then every ${botSettings.intervalMinutes} minute(s)`);
                    // Fire the first cycle immediately instead of waiting a full interval
                    executeAutomaticTrade();
                  } else {
                    setIsBotRunning(false);
                    addNotification('info', 'Bot stopped');
                  }
                }}
                className={`flex-1 flex items-center justify-center space-x-3 px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                  isBotRunning 
                    ? 'btn-danger' 
                    : 'btn-success'
                }`}
              >
                {isBotRunning ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                <span>{isBotRunning ? 'Stop Bot' : 'Start Bot'}</span>
              </button>
              
              <button
                onClick={() => executeAutomaticTrade()}
                disabled={!tradePassword}
                title={!tradePassword ? 'Enter your trade password (top-right) to trade' : 'Run one SELL → BUY cycle now'}
                className="flex-1 flex items-center justify-center space-x-3 px-6 py-3 btn-purple disabled:bg-gray-600 disabled:cursor-not-allowed disabled:transform-none"
              >
                <RefreshCw className="w-5 h-5" />
                <span>⚡ FLASH Trade: SELL (Random) → BUY (Same Amount)</span>
              </button>
            </div>
          </div>
        </div>

        {/* Ultra Low Fee Settings */}
        <div className="space-y-4">
          <div className="bg-gray-900 rounded-xl p-4">
            <h4 className="text-white font-medium mb-3 flex items-center">
              <Gauge className="w-4 h-4 mr-2 text-blue-400" />
              Ultra Low Fee Settings
            </h4>
            
            <div className="space-y-3">
              <div>
                <label className="block text-gray-400 text-xs mb-1">Trading Interval</label>
                <select
                  value={botSettings.intervalMinutes}
                  onChange={(e) => setBotSettings(prev => ({
                    ...prev,
                    intervalMinutes: parseInt(e.target.value)
                  }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={15}>15 Minutes</option>
                  <option value={30}>30 Minutes</option>
                  <option value={60}>1 Hour</option>
                  <option value={90}>90 Minutes</option>
                  <option value={120}>2 Hours</option>
                  <option value={240}>4 Hours (Recommended)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-400 text-xs mb-1">Slippage Tolerance (%)</label>
                <select
                  value={botSettings.slippage}
                  onChange={(e) => setBotSettings(prev => ({
                    ...prev,
                    slippage: e.target.value
                  }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="1">1% (Ultra Conservative)</option>
                  <option value="3">3% (Normal) - RECOMMENDED</option>
                  <option value="5">5% (Aggressive)</option>
                  <option value="10">10% (Very Aggressive)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-400 text-xs mb-1">Gas Price Strategy</label>
                <select
                  value={botSettings.gasSettings.gasPrice}
                  onChange={(e) => setBotSettings(prev => ({
                    ...prev,
                    gasSettings: { ...prev.gasSettings, gasPrice: e.target.value }
                  }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ultraLow">Ultra Low (3 Gwei) - RECOMMENDED</option>
                  <option value="low">Low (5 Gwei)</option>
                  <option value="normal">Normal (10 Gwei)</option>
                  <option value="high">High (15 Gwei)</option>
                  <option value="emergency">Emergency (20 Gwei)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-400 text-xs mb-1">Max Fee per TX (BNB)</label>
                <input
                  type="number"
                  step="0.001"
                  value={botSettings.gasSettings.maxFeePerTx}
                  onChange={(e) => setBotSettings(prev => ({
                    ...prev,
                    gasSettings: { ...prev.gasSettings, maxFeePerTx: e.target.value }
                  }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex items-center space-x-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={botSettings.gasSettings.onlyOffPeak}
                    onChange={(e) => setBotSettings(prev => ({
                      ...prev,
                      gasSettings: { ...prev.gasSettings, onlyOffPeak: e.target.checked }
                    }))}
                    className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-gray-400 text-sm">Only Off-Peak Hours</span>
                </label>
                
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={botSettings.gasSettings.waitForLowFees}
                    onChange={(e) => setBotSettings(prev => ({
                      ...prev,
                      gasSettings: { ...prev.gasSettings, waitForLowFees: e.target.checked }
                    }))}
                    className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-gray-400 text-sm">Wait for Low Fees</span>
                </label>
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={botSettings.gasSettings.emergencyStop}
                    onChange={(e) => setBotSettings(prev => ({
                      ...prev,
                      gasSettings: { ...prev.gasSettings, emergencyStop: e.target.checked }
                    }))}
                    className="rounded border-gray-600 bg-gray-800 text-red-500 focus:ring-red-500"
                  />
                  <span className="text-red-400 text-sm">Emergency Stop</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Enhanced Trading Schedule
  const TradingSchedule = () => (
    <div className="card-modern">
      <h3 className="text-white text-lg font-bold mb-4 flex items-center">
        <Clock className="w-5 h-5 mr-2 text-purple-400" />
        Trading Schedule
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-gray-400 font-medium mb-3">Manual Trading</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-gray-400 text-sm mb-2">Trade Amount (BNB)</label>
              <input
                type="number"
                step="0.01"
                value={manualTradeAmount}
                onChange={(e) => setManualTradeAmount(e.target.value)}
                className="input-modern w-full"
              />
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={async () => {
                  try {
                    // Real on-chain buy using the manual amount; works while auto-trading is running too
                    const result = await executeBuyOrder(manualTradeAmount);
                    if (result) {
                      setTimeout(() => { forceUpdateProfitLoss(); }, 2000);
                    }
                  } catch (error) {
                    // executeBuyOrder already surfaces its own error notification
                  }
                }}
                disabled={!tradePassword}
                className="flex-1 btn-success disabled:bg-gray-600 disabled:cursor-not-allowed disabled:transform-none"
              >
                Buy {currentToken.symbol}
              </button>
              <button
                onClick={async () => {
                  try {
                    // Real on-chain sell using the manual amount; works while auto-trading is running too
                    const result = await executeSellOrder(manualTradeAmount);
                    if (result) {
                      setTimeout(() => { forceUpdateProfitLoss(); }, 2000);
                    }
                  } catch (error) {
                    // executeSellOrder already surfaces its own error notification
                  }
                }}
                disabled={!tradePassword}
                className="flex-1 btn-danger disabled:cursor-not-allowed disabled:transform-none"
              >
                Sell {currentToken.symbol}
              </button>
            </div>
            
            {/* Auto Trade Button */}
            <div className="mt-4">
              <button
                onClick={() => executeAutomaticTrade()}
                disabled={!tradePassword}
                className="w-full btn-purple disabled:bg-gray-600 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-3"
              >
                <RefreshCw className="w-5 h-5" />
                <span>⚡ FLASH Trade: SELL (Random) → BUY (Same Amount)</span>
              </button>
            </div>
            
            {/* Balance Info */}
            <div className="mt-3 p-3 bg-gray-900 rounded-lg">
              <div className="text-xs text-gray-400 mb-2">Current Balances:</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-400">BNB:</span>
                  <span className="text-white ml-1">{balances.bnb} BNB</span>
                </div>
                <div>
                  <span className="text-gray-400">{currentToken.symbol}:</span>
                  <span className="text-white ml-1">{balances.token} {currentToken.symbol}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div>
          <h4 className="text-gray-400 font-medium mb-3">Network Status</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Current Gas:</span>
              <span className={`font-medium ${
                networkStatus.isUltraLowFee ? 'text-green-400' :
                networkStatus.isLowFee ? 'text-yellow-400' : 'text-red-400'
              }`}>
                {networkStatus.gasPriceGwei} Gwei
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Off-Peak Hours:</span>
              <span className={networkStatus.isOffPeak ? 'text-green-400' : 'text-red-400'}>
                {networkStatus.isOffPeak ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">RPC Status:</span>
              <span className="text-green-400">Connected</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Block Height:</span>
              <span className="text-white">#{networkStatus.blockNumber}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Enhanced Profit/Loss Report
  const ProfitLossReport = () => (
    <div className="card-modern">
      <h3 className="text-white text-lg font-bold mb-4 flex items-center">
        <TrendingUp className="w-5 h-5 mr-2 text-green-400" />
        Profit & Loss Report
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* BNB P&L */}
        <div className="bg-gray-900 rounded-xl p-4">
          <h4 className="text-gray-400 font-medium mb-3">BNB Performance</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Initial:</span>
              <span className="text-white">{profitLoss.bnb.initial} BNB</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Current:</span>
              <span className="text-white">{profitLoss.bnb.current} BNB</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Change:</span>
              <span className={`font-medium ${profitLoss.bnb.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                {profitLoss.bnb.change} BNB ({profitLoss.bnb.changePercent}%)
              </span>
            </div>
          </div>
        </div>
        
        {/* Token P&L */}
        <div className="bg-gray-900 rounded-xl p-4">
          <h4 className="text-gray-400 font-medium mb-3">{currentToken.symbol} Performance</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Initial:</span>
              <span className="text-white">{profitLoss.token.initial} {currentToken.symbol}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Current:</span>
              <span className="text-white">{profitLoss.token.current} {currentToken.symbol}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Change:</span>
              <span className={`font-medium ${profitLoss.token.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                {profitLoss.token.change} {currentToken.symbol} ({profitLoss.token.changePercent}%)
              </span>
            </div>
          </div>
        </div>
        
        {/* Total USD P&L */}
        <div className="bg-gray-900 rounded-xl p-4">
          <h4 className="text-gray-400 font-medium mb-3">Total USD Performance</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Initial:</span>
              <span className="text-white">${profitLoss.totalUSD.initial}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Current:</span>
              <span className="text-white">${profitLoss.totalUSD.current}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Change:</span>
              <span className={`font-medium ${profitLoss.totalUSD.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                ${profitLoss.totalUSD.change} ({profitLoss.totalUSD.changePercent}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Two P&L views: (1) price movement, (2) measured trading cost */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 1) Price P&L — value change from price alone (deposit-proof) */}
        <div className="bg-gray-900 rounded-xl p-4">
          <h4 className="text-gray-400 font-medium mb-1">Fiyat P&amp;L — {currentToken.symbol} + BNB</h4>
          <p className="text-xs text-gray-600 mb-3">Coin fiyatlarinin hareketinden gelen kazanc/kayip</p>
          {pricePnl.ready ? (
            <>
              <div className={`text-2xl font-bold ${pricePnl.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                {pricePnl.isPositive ? '+' : ''}${pricePnl.usd.toFixed(2)}
              </div>
              <div className={`text-sm mt-1 ${pricePnl.isPositive ? 'text-green-400' : 'text-red-400'}`}>
                {pricePnl.isPositive ? '+' : ''}{pricePnl.pct.toFixed(2)}%
                <span className="text-gray-600"> — baslangictan bu yana</span>
              </div>
            </>
          ) : (
            <div className="text-gray-500 text-xs">Fiyat baslangici bir sonraki guncellemede yakalanacak</div>
          )}
        </div>

        {/* 2) Measured trading cost — server counters, both legs + gas */}
        <div className="bg-gray-900 rounded-xl p-4">
          <h4 className="text-gray-400 font-medium mb-1">Islem Maliyeti — olculmus</h4>
          <p className="text-xs text-gray-600 mb-3">Alsat yaptikca asinan: vergi + havuz komisyonu + kayma + gas (BNB ve token tarafi)</p>
          <div className="space-y-2 text-sm">
            {[
              ['Bugun (UTC)', pnlView.tradeCostBNB.daily],
              ['Bu ay', pnlView.tradeCostBNB.monthly],
              [serverBot.costSince ? `Toplam (${new Date(serverBot.costSince).toLocaleDateString()}'den beri)` : 'Toplam', pnlView.tradeCostBNB.allTime]
            ].map(([label, bnb]) => (
              <div key={label} className="flex justify-between">
                <span className="text-gray-400">{label}:</span>
                <span className={`font-medium ${bnb >= 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {bnb >= 0 ? '-' : '+'}{Math.abs(bnb).toFixed(6)} BNB ({bnb >= 0 ? '-' : '+'}${Math.abs(bnb * bnbUsdPrice).toFixed(2)})
                </span>
              </div>
            ))}
          </div>
          {pnlView.tradeCostBNB.allTime === 0 && (
            <p className="text-xs text-gray-600 mt-2">Sayaclar ilk islemden itibaren birikir (gecmise donuk veri yok).</p>
          )}
        </div>
      </div>

      <div className="mt-4 p-3 bg-gray-900 rounded-lg flex items-center justify-between">
        <div className="text-xs text-gray-400">
          {initialBalances.timestamp
            ? `Tracking started: ${initialBalances.timestamp.toLocaleString()}`
            : 'Tracking baseline will be captured on first balance load'}
        </div>
        <button
          type="button"
          onClick={handleResetTracking}
          className="text-xs text-gray-400 hover:text-red-400 border border-gray-700 rounded-lg px-3 py-1"
          title="Clear P&L baseline, history and stats (does not touch your wallet)"
        >
          Reset tracking
        </button>
      </div>
    </div>
  );

  // Enhanced Trading History with Fee Information
  const TradingHistory = () => (
    <div className="card-modern">
      <h3 className="text-white text-lg font-bold mb-4 flex items-center">
        <Activity className="w-5 h-5 mr-2 text-green-400" />
        Trading History
      </h3>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 text-gray-400 font-medium">Date & Time</th>
              <th className="text-left py-3 text-gray-400 font-medium">Token</th>
              <th className="text-left py-3 text-gray-400 font-medium">Type</th>
              <th className="text-left py-3 text-gray-400 font-medium">Amount</th>
              <th className="text-left py-3 text-gray-400 font-medium">Price</th>
              <th className="text-left py-3 text-gray-400 font-medium">Fee</th>
              <th className="text-left py-3 text-gray-400 font-medium">Savings</th>
              <th className="text-left py-3 text-gray-400 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {tradingHistory.map((trade) => (
              <tr key={trade.id} className="border-b border-gray-700 hover:bg-gray-750">
                <td className="py-3 text-gray-300">{trade.time}</td>
                <td className="py-3 text-white font-medium">{trade.token || 'PAYU'}</td>
                <td className="py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    trade.type === 'BUY' ? 'bg-green-900 text-green-400' : 'bg-red-900 text-red-400'
                  }`}>
                    {trade.type}
                  </span>
                </td>
                <td className="py-3 text-white">{trade.amount}</td>
                <td className="py-3 text-gray-300">{trade.price}</td>
                <td className="py-3 text-gray-300">{trade.fee || '0.000'} BNB</td>
                <td className="py-3 text-green-400">{trade.savings || '0.000'} BNB</td>
                <td className="py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    trade.status === 'Completed' ? 'bg-green-900 text-green-400' : 'bg-yellow-900 text-yellow-400'
                  }`}>
                    {trade.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Headline P&L = price P&L (price movement on current holdings). The old
  // balance-vs-baseline figure counted coins deposited into the wallet as profit.
  const pnlPositive = pricePnl.isPositive;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      <Header />
      <NotificationCenter />

      <main className="container mx-auto px-6 py-8">
        {/* Stats Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="BNB Balance"
            value={`$${balances.bnbUSDValue || '0.00'}`}
            subValue={`${balances.bnb} BNB`}
            icon={DollarSign}
            color="from-green-500 to-green-600"
          />
          <StatsCard
            title={`${currentToken.symbol} Balance`}
            value={`$${balances.tokenUSDValue || '0.00'}`}
            subValue={`${balances.token} ${currentToken.symbol}`}
            icon={DollarSign}
            color="from-blue-500 to-blue-600"
          />
          <StatsCard
            title="Fiyat P&L"
            value={pricePnl.ready ? `${pnlPositive ? '+' : '-'}$${Math.abs(pricePnl.usd).toFixed(2)}` : '—'}
            subValue={pricePnl.ready ? `${pricePnl.pct.toFixed(2)}% fiyat hareketi (depozito haric)` : 'Baslangic fiyati bekleniyor'}
            icon={pnlPositive ? TrendingUp : TrendingDown}
            color={pnlPositive ? 'from-green-500 to-green-600' : 'from-red-500 to-red-600'}
            trend={pricePnl.ready ? parseFloat(pricePnl.pct.toFixed(2)) : null}
          />
          <StatsCard
            title="Network Status"
            value={networkStatus.blockNumber > 0 ? 'Connected' : 'Connecting...'}
            subValue={`Gas: ${networkStatus.gasPriceGwei || '0'} Gwei · Block #${networkStatus.blockNumber || 0}`}
            icon={Activity}
            color={networkStatus.blockNumber > 0 ? 'from-green-500 to-green-600' : 'from-yellow-500 to-yellow-600'}
          />
        </div>
        
        {/* Main Content */}
        <div className="space-y-6">
          <BotControlPanel />
          <TradingSchedule />
          <ProfitLossReport />
          <TradingHistory />
        </div>
      </main>
    </div>
  );
};

export default CryptoTradingApp; 